/**
 * Task #58 — Policy version constants.
 *
 * Single source of truth for the version strings stored in
 * `profiles.privacy_acknowledged_version` and
 * `profiles.terms_acknowledged_version`. The /profile API echoes these
 * constants in its response so the frontend can compare without keeping
 * its own copy.
 *
 * IMPORTANT: bump these strings whenever you make a *material* change to
 * `artifacts/classtrack/src/pages/privacy.tsx` or
 * `artifacts/classtrack/src/pages/terms.tsx`. Bumping triggers a re-prompt
 * for every existing user on their next login (the AuthGuard sees that
 * `profile.privacyAcknowledgedVersion !== currentPrivacyVersion` and
 * routes to /acknowledge). Cosmetic edits (typo fixes, link target
 * updates, formatting) do NOT require a bump.
 *
 * Format: "YYYY-MM" matching the "Last updated" date in the document.
 */
export const PRIVACY_VERSION = "2026-05-15";
export const TERMS_VERSION = "2026-05";

/**
 * Task #101 — Human-written "what changed" summaries shown on the
 * /acknowledge re-prompt screen so teachers who get bounced mid-flow
 * understand *why* they're being asked to tick the box again.
 *
 * Keys MUST match the version string in PRIVACY_VERSION / TERMS_VERSION
 * exactly. When you bump a version, add a new entry here describing
 * what materially changed in plain language ("We added a section on
 * AI redaction", not changelog-speak). If you genuinely have nothing
 * to say, you may omit the entry — the UI falls back to "Minor
 * updates." — but please prefer one short sentence.
 */
export const PRIVACY_CHANGES: Record<string, string> = {
  "2026-05":
    "We clarified what head teachers at your school can see and added a section on the optional AI features and best-effort redaction.",
  "2026-05-15":
    "We updated the data storage section to reflect that your records, classes, students, and diary entries are now stored in Australia (AWS Sydney). The API server and uploaded files remain on US infrastructure — this is clearly explained in the updated policy.",
};

export const TERMS_CHANGES: Record<string, string> = {
  "2026-05":
    "We added a Teacher responsibilities section covering school policy checks, using the least identifying student label, and transferring records into your school's official system.",
};

const FALLBACK_CHANGE_SUMMARY = "Minor updates.";

export function getPrivacyChangeSummary(version: string): string {
  return PRIVACY_CHANGES[version] ?? FALLBACK_CHANGE_SUMMARY;
}

export function getTermsChangeSummary(version: string): string {
  return TERMS_CHANGES[version] ?? FALLBACK_CHANGE_SUMMARY;
}
